"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Plus,
  Calendar,
  Receipt,
  MessageSquare,
  MessageCircle,
  LineChart,
  CalendarCheck,
  Hotel,
  Image as ImageIcon,
  Users,
  Tag,
  ArrowRight,
} from "lucide-react"
import { AdminLayout } from "./AdminLayout"
import { useAuth } from "../hooks/useAuth"
import { ref, onValue } from "../lib/firebase-database"
import { auth, db } from "../firebase"
import { useQueryCount } from "../hooks/useQueryCount"
import { useAdminQueryCount } from "../hooks/useAdminQueryCount"

function StatCard({ label, value, hint }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
    </div>
  )
}

function ActionTile({ icon: Icon, label, description, onClick, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-black"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-9 h-9 rounded-md bg-gray-100 flex items-center justify-center text-gray-700 group-hover:bg-black group-hover:text-white">
          <Icon className="h-4 w-4" />
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-black" />
      </div>
      <div className="mt-3">
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        {description && (
          <div className="mt-0.5 text-xs text-gray-500 line-clamp-2">
            {description}
          </div>
        )}
      </div>
      {badge > 0 && (
        <span className="absolute top-3 right-10 text-[11px] font-medium rounded-full px-1.5 py-0.5 min-w-[20px] text-center bg-black text-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  )
}

export default function AdminDashboard() {
  const { user, isAdmin } = useAuth()
  const navigate = useNavigate()
  const { newQueriesCount } = useQueryCount()
  const { newQueryCount } = useAdminQueryCount()
  const [counts, setCounts] = useState({ villas: 0, owners: 0, bookings: 0 })

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate("/")
    }
  }, [user, isAdmin, navigate])

  useEffect(() => {
    const villasRef = ref(db, "villas")
    const unsubVillas = onValue(villasRef, (snap) => {
      const data = snap.val() || {}
      const villas = Object.values(data)
      const ownerEmails = new Set(
        villas.map((v) => v?.ownerEmail).filter(Boolean)
      )
      setCounts((prev) => ({
        ...prev,
        villas: villas.length,
        owners: ownerEmails.size,
      }))
    })

    const bookingsRef = ref(db, "bookings")
    const unsubBookings = onValue(bookingsRef, (snap) => {
      const data = snap.val() || {}
      setCounts((prev) => ({ ...prev, bookings: Object.keys(data).length }))
    })

    return () => {
      if (typeof unsubVillas === "function") unsubVillas()
      if (typeof unsubBookings === "function") unsubBookings()
    }
  }, [])

  const handleLogout = () => {
    auth.signOut().then(() => {
      navigate("/login")
    })
  }

  const greeting = (() => {
    const h = new Date().getHours()
    if (h < 12) return "Good morning"
    if (h < 17) return "Good afternoon"
    return "Good evening"
  })()

  const actions = [
    {
      icon: Plus,
      label: "Add New Villa",
      description: "List a new property on VillaKosh",
      to: "/add-property",
    },
    {
      icon: Hotel,
      label: "Manage Villas",
      description: "View, edit, hide or delete listings",
      to: "/admin/hotels",
    },
    {
      icon: Calendar,
      label: "Bookings",
      description: "Review and manage all bookings",
      to: "/admin/bookings",
    },
    {
      icon: CalendarCheck,
      label: "Availability",
      description: "Block or open villa dates",
      to: "/admin/availability",
    },
    {
      icon: ImageIcon,
      label: "Villa Images",
      description: "Upload and organize property images",
      to: "/admin/villa-images",
    },
    {
      icon: LineChart,
      label: "Analytics",
      description: "Visitors, inquiries and conversions",
      to: "/admin/analytics",
    },
    {
      icon: Receipt,
      label: "Billing",
      description: "Invoices and owner payouts",
      to: "/admin/billing",
    },
    {
      icon: Tag,
      label: "Offers & Discounts",
      description: "Promotions across listings",
      to: "/admin/offers-discounts",
    },
    {
      icon: MessageSquare,
      label: "Admin Queries",
      description: "Messages from property owners",
      to: "/admin/queries",
      badge: newQueryCount,
    },
    {
      icon: MessageCircle,
      label: "User Queries",
      description: "Messages and contact requests",
      to: "/admin/users-queries",
      badge: newQueriesCount,
    },
    {
      icon: Users,
      label: "Property Owners",
      description: "Owner directory and contact info",
      to: "/admin/users",
    },
  ]

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-8">
        <div>
          <p className="text-sm text-gray-500">{greeting},</p>
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">
            {user?.displayName || "Admin"}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Here's a quick look at your VillaKosh control panel.
          </p>
        </div>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Villas" value={counts.villas} />
          <StatCard label="Property Owners" value={counts.owners} />
          <StatCard label="Total Bookings" value={counts.bookings} />
          <StatCard
            label="Pending Queries"
            value={newQueryCount + newQueriesCount}
            hint="Admin + user queries"
          />
        </section>

        <section>
          <div className="flex items-end justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
              Quick Actions
            </h2>
          </div>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {actions.map((a) => (
              <ActionTile
                key={a.to}
                icon={a.icon}
                label={a.label}
                description={a.description}
                badge={a.badge}
                onClick={() => navigate(a.to)}
              />
            ))}
          </div>
        </section>
      </div>
    </AdminLayout>
  )
}
